import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for Web Speech API voice-to-text dictation.
 * Appends recognised speech to the current value via onChange callback.
 */
const useVoiceInput = (value, onChange) => {
  const [isListening, setIsListening] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recognitionRef = useRef(null);
  const valueRef = useRef(value);
  const timerRef = useRef(null);

  // Keep ref in sync so the callback always sees the latest value
  useEffect(() => { valueRef.current = value; }, [value]);

  // Recording timer
  useEffect(() => {
    if (isListening) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isListening]);

  const supported = typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const wantListeningRef = useRef(false);

  const startListening = useCallback(() => {
    if (!supported) return;
    wantListeningRef.current = true;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.continuous = true;

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript) {
        const current = valueRef.current || '';
        const separator = current && !current.endsWith('\n') && !current.endsWith(' ') ? ' ' : '';
        onChange(current + separator + transcript);
      }
    };

    recognition.onerror = (event) => {
      // 'no-speech' and 'aborted' are normal — just let onend restart
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
        wantListeningRef.current = false;
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      // Auto-restart if user hasn't explicitly stopped
      if (wantListeningRef.current) {
        try {
          recognition.start();
        } catch (e) {
          // Already started or other issue — give up
          wantListeningRef.current = false;
          setIsListening(false);
        }
        return;
      }
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [supported, onChange]);

  const stopListening = useCallback(() => {
    wantListeningRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const toggle = useCallback(() => {
    if (isListening) stopListening();
    else startListening();
  }, [isListening, startListening, stopListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const formatElapsed = () => {
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return { isListening, toggle, supported, elapsed, formatElapsed };
};

export default useVoiceInput;
